import React from 'react';
import { Box, Text, Button } from '@indeed/ifl-components';
import { Close } from '@indeed/ifl-icons';
import { jsProThemeButton } from '../jsProTheme/jsProTheme';
import useGettext from '../../../../util/GettextFactory';
import { useInitialState } from '../../../../lib/context/InitialStateContext';

export interface StandalonePageHeaderProps {
    /** Title displayed in the center */
    title: string;
    /** Called when close button is clicked */
    onClose: () => void;
}

/**
 * Reusable header component for standalone Auto Apply pages
 * Similar to AutoApplySetupNavigation but simplified for standalone pages
 *
 * Features:
 * - Centered title with close button
 * - CSS Grid layout for proper centering
 * - Follows AutoApplySetupNavigation styling patterns
 */
export const StandalonePageHeader: React.FC<StandalonePageHeaderProps> = ({ title, onClose }) => {
    const gt = useGettext();
    const { isJobSearchApp } = useInitialState();

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto 1fr',
                alignItems: 'center',
            }}
        >
            {/* Left: Empty space for grid balance */}
            <Box />

            {/* Center: Title */}
            <Text
                level={3}
                variant="contrast"
                sx={{
                    fontWeight: 700,
                    textAlign: 'center',
                }}
            >
                {title}
            </Text>

            {/* Right: Close button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="tertiary"
                    type="button"
                    onClick={onClose}
                    aria-label={gt.gettext('Close')}
                    sx={{
                        ...jsProThemeButton.tertiary,
                        // Use visibility to keep the layout consistent in Job Search app
                        visibility: isJobSearchApp ? 'hidden' : undefined,
                    }}
                >
                    <Close size="md" aria-hidden />
                </Button>
            </Box>
        </Box>
    );
};
